import { useCallback, useEffect, useMemo, useRef } from "react";

import { Locale as RouterLocale, useRouter } from "next/router";

import { Locale as GatewayLocale, Language } from "@/Apollo/schema";
import { isBrowser } from "@/common/utils/general.utils";
import { objectKeys } from "@/common/utils/object.utils";
import { cleanSearchParams } from "@/common/utils/router.utils";
import { parseRelativePath } from "@/common/utils/url.utils";
import { type ContentLocale, toContentLocale } from "@/i18n/config";

const EXCLUDE_BANNER_ROUTES = [
  "/create-ad",
  "/edit-ad",
  "/checkout",
  "/checkout2",
  "/cart",
  "/cart2",
  "/chat",
];

export const useShouldExcludeBanner = () => {
  const { asPath } = useRouter();

  return EXCLUDE_BANNER_ROUTES.some(route => asPath.includes(route));
};

/** The gateway's `Language` per content language; `en-us` reads `en` (`useLanguage`, WHO-5766). */
export const ROUTER_LOCALE_LANGUAGE_MAP: Record<ContentLocale, Language> = {
  en: Language.En,
  nl: Language.Nl,
  de: Language.De,
  fr: Language.Fr,
  es: Language.Es,
  it: Language.It,
};

type GatewayLocaleKeys =
  | GatewayLocale.EnUs
  | GatewayLocale.NlNl
  | GatewayLocale.DeDe
  | GatewayLocale.FrFr
  | GatewayLocale.EsEs
  | GatewayLocale.ItIt;

export const GATEWAY_LOCALE_LANGUAGE_MAP: Record<GatewayLocaleKeys, Language> =
  {
    [GatewayLocale.EnUs]: Language.En,
    [GatewayLocale.NlNl]: Language.Nl,
    [GatewayLocale.DeDe]: Language.De,
    [GatewayLocale.FrFr]: Language.Fr,
    [GatewayLocale.EsEs]: Language.Es,
    [GatewayLocale.ItIt]: Language.It,
  };

export const useLanguage = () => {
  const { locale } = useRouter();
  return { language: ROUTER_LOCALE_LANGUAGE_MAP[toContentLocale(locale)] };
};

export const useLocalizedLanguages = (locale: RouterLocale) => {
  return useMemo(() => {
    const languageNames = new Intl.DisplayNames([locale], {
      type: "language",
    });

    return objectKeys(GATEWAY_LOCALE_LANGUAGE_MAP).map(locale => ({
      title: languageNames.of(GATEWAY_LOCALE_LANGUAGE_MAP[locale]),
      language: GATEWAY_LOCALE_LANGUAGE_MAP[locale],
      locale: locale,
    }));
  }, [locale]);
};

/**
 * Used for creating page URL with pagination params.
 *
 * @returns Functiona that generates new page URL based on the current one..
 */
export const useURLPagination = () => {
  const router = useRouter();

  const getURLString = (page: number) => {
    const url = parseRelativePath(router.asPath);

    if (page === 1) {
      url.searchParams.delete("page");
    } else {
      url.searchParams.set("page", page.toString());
    }

    // Clean search params
    cleanSearchParams(url.searchParams);

    return `${url.pathname}${url.search}`;
  };

  return { getURLString };
};

type UseRouteChangeOptions = {
  onPathnameChanged?: (pathname: string) => void | Promise<void>;
  onRouteChangeStart?: (url: string, shallow: boolean) => void | Promise<void>;
  onRouteChangeComplete?: (
    url: string,
    shallow: boolean
  ) => void | Promise<void>;
};

/**
 * Calls `on` function whenever windows's `pathname` changes.
 *
 * @param on Function to call.
 */
export const useRouteChange = ({
  onPathnameChanged,
  onRouteChangeStart,
  onRouteChangeComplete,
}: UseRouteChangeOptions) => {
  const lastPathnameRef = useRef<string | null>(null);
  const { events } = useRouter();

  const handleRouteChangeStart = useCallback(
    (url: string, { shallow }: { shallow: boolean }) => {
      onRouteChangeStart?.(url, shallow);
    },
    [onRouteChangeStart]
  );

  const handleRoutePathnameChanged = useCallback(() => {
    const newPathname = window.location.pathname;

    if (lastPathnameRef.current !== newPathname) {
      lastPathnameRef.current = newPathname;
      onPathnameChanged?.(lastPathnameRef.current);
    }
  }, [onPathnameChanged]);

  const handleRouteChangeComplete = useCallback(
    (url: string, { shallow }: { shallow: boolean }) => {
      if (!isBrowser()) {
        return;
      }

      onRouteChangeComplete?.(url, shallow);
      handleRoutePathnameChanged();
    },
    [handleRoutePathnameChanged, onRouteChangeComplete]
  );

  useEffect(() => {
    handleRoutePathnameChanged();

    events.on("routeChangeStart", handleRouteChangeStart);
    events.on("routeChangeComplete", handleRouteChangeComplete);

    /** Unmounted - cleanup. */
    return () => {
      events.off("routeChangeStart", handleRouteChangeStart);
      events.off("routeChangeComplete", handleRouteChangeComplete);
    };
  }, [
    events,
    handleRouteChangeComplete,
    handleRouteChangeStart,
    handleRoutePathnameChanged,
  ]);
};
